import React, { useContext, useMemo, useState } from "react"
import { Pressable, Text, View } from "react-native"
import { textStyles } from "../../style"
import { ThemeContext } from "../../utility/context/ThemeContext"
import LightweightPieChart from "./lightweight-pie-chart"

/** Minimum width of the social chart container for pie + legend to lay out in a row. */
const SOCIAL_IMPACT_ROW_MIN_CONTAINER_WIDTH = 500

export type SocialImpactSurface = "inApp" | "embed"

export type SocialImpactChartProps = {
  chartData: {
    Color: string
    Title: string
    Value: number
  }[]
  /** In-app savings card vs iframe embed: layout, typography, and width behavior differ. */
  surface?: SocialImpactSurface
}

const socialImpactHex = (raw: string | undefined): string => {
  let h = String(raw ?? "").replace("#", "").trim()
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("")
  }
  if (/^[0-9a-fA-F]{6}$/.test(h)) {
    return h
  }
  return "2563eb"
}

export const SocialImpactChart = ({
  chartData,
  surface = "inApp",
}: SocialImpactChartProps): JSX.Element => {
  const isEmbed = surface === "embed"
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [pieSlotW, setPieSlotW] = useState(0)
  const [containerW, setContainerW] = useState(0)
  const theme = useContext(ThemeContext).theme
  const { textMain, textAlt, primary } = theme.colors
  const tableBorder = `${textAlt}55`

  const unfocusFromChart = () => {
    setFocusedIndex(-1)
  }

  const rows = useMemo(
    () => (chartData || []).filter((r) => Number(r?.Value) > 0),
    [chartData]
  )

  const pieData = useMemo(
    () =>
      rows.map((row, index) => {
        const six = socialImpactHex(row.Color)
        const dim = focusedIndex !== -1 && focusedIndex !== index
        return {
          value: Number(row.Value),
          color: dim ? `#${six}40` : `#${six}`,
          key: `si-${index}-${row.Title}`,
        }
      }),
    [rows, focusedIndex]
  )

  const pieSize =
    pieSlotW > 0 ? Math.max(120, Math.min(Math.floor(pieSlotW), 280)) : 200

  const moneyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
    []
  )

  const rowHighlight =
    theme.name === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"

  if (rows.length === 0) {
    return (
      <View style={{ width: "100%" }}>
        <Text style={[textStyles.body, { color: textAlt }]}>
          No social impact data available yet.
        </Text>
      </View>
    )
  }

  const isWide =
    isEmbed || containerW > SOCIAL_IMPACT_ROW_MIN_CONTAINER_WIDTH

  const chartColStyle = isEmbed
    ? ({
        width: 280,
        flexShrink: 0,
        justifyContent: "center",
        alignSelf: "stretch",
      } as const)
    : isWide
      ? ({ flex: 1, minWidth: 0, justifyContent: "center" } as const)
      : ({ width: "100%" } as const)

  const legendColStyle = isEmbed
    ? ({
        flexShrink: 1,
        minWidth: 200,
        maxWidth: 420,
        alignSelf: "stretch",
      } as const)
    : isWide
      ? ({ flex: 1, minWidth: 0 } as const)
      : ({ width: "100%" } as const)

  const rootStyle = isEmbed
    ? ({
        alignSelf: "flex-start",
        minWidth: SOCIAL_IMPACT_ROW_MIN_CONTAINER_WIDTH + 1,
      } as const)
    : ({ width: "100%" } as const)

  const innerStackStyle = isEmbed
    ? ({
        alignSelf: "flex-start",
        width: "auto" as const,
        flexDirection: "column" as const,
        gap: 4,
      } as const)
    : ({
        width: "100%",
        flexDirection: "column" as const,
        gap: 4,
      } as const)

  const pieLegendRowStyle = isEmbed
    ? ({
        flexDirection: "row" as const,
        gap: 12,
        alignItems: "stretch" as const,
        alignSelf: "flex-start" as const,
        width: "auto" as const,
      } as const)
    : ({
        width: "100%",
        flexDirection: isWide ? "row" : "column",
        gap: isWide ? 12 : 8,
        alignItems: "stretch",
      } as const)

  return (
    <View
      style={rootStyle}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width)
        if (w !== containerW) {
          setContainerW(w)
        }
      }}
    >
      <View style={innerStackStyle}>
        <Text
          style={
            isEmbed
              ? [textStyles.body, { color: textAlt, alignSelf: "flex-start" }]
              : [textStyles.h4, { color: primary, width: "100%" }]
          }
        >
          Amount in Mission-Aligned Loans Financed Since Inception
        </Text>
        <View style={pieLegendRowStyle}>
          <View style={chartColStyle}>
            <View
              style={{
                width: "100%",
                minHeight: 140,
                alignItems: "center",
                justifyContent: "center",
              }}
              onLayout={(e) => {
                const w = e.nativeEvent.layout.width
                if (w && Math.round(w) !== pieSlotW) {
                  setPieSlotW(Math.round(w) * 0.66)
                }
              }}
            >
              <LightweightPieChart
                data={pieData}
                size={pieSize}
                innerRadius={0}
                onSliceHover={(index) => setFocusedIndex(index)}
                onSliceLeave={unfocusFromChart}
              />
            </View>
          </View>

          <View
            style={[
              legendColStyle,
              {
                borderWidth: 1,
                borderColor: tableBorder,
                borderRadius: 10,
                overflow: "hidden",
              },
            ]}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-end",
                borderBottomWidth: 1,
                borderBottomColor: tableBorder,
                paddingVertical: 5,
                paddingHorizontal: 4,
              }}
            >
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: "700",
                  color: textAlt,
                  paddingLeft: 4,
                  paddingRight: 8,
                }}
              >
                Impact Area
              </Text>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: textAlt,
                  paddingRight: 4,
                  textAlign: "right",
                  fontVariant: ["tabular-nums"],
                }}
              >
                $
              </Text>
            </View>
            {rows.map((row, i) => {
              const six = socialImpactHex(row.Color)
              const amount = moneyFmt.format(Math.round(Number(row.Value || 0)))
              const active = focusedIndex === i
              const faded = focusedIndex !== -1 && !active
              return (
                <Pressable
                  key={pieData[i]?.key ?? `si-row-${i}`}
                  onHoverIn={() => setFocusedIndex(i)}
                  onHoverOut={unfocusFromChart}
                  onPressIn={() => setFocusedIndex(i)}
                  onPressOut={unfocusFromChart}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    borderBottomWidth: i < rows.length - 1 ? 1 : 0,
                    borderBottomColor: tableBorder,
                    backgroundColor: active ? rowHighlight : "transparent",
                    opacity: faded ? 0.4 : 1,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      minWidth: 0,
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      paddingVertical: 4,
                      paddingLeft: 4,
                      paddingRight: 8,
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: `#${six}`,
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        flexShrink: 0,
                      }}
                    />
                    <Text
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        lineHeight: 16,
                        fontWeight: "700",
                        color: textMain,
                        fontFamily: "poppins-semi-bold",
                      }}
                    >
                      {row.Title}
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      fontWeight: "700",
                      color: textMain,
                      fontFamily: "poppins-semi-bold",
                      paddingVertical: 4,
                      paddingRight: 4,
                      paddingLeft: 0,
                      fontVariant: ["tabular-nums"],
                    }}
                  >
                    {amount}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        </View>
      </View>
    </View>
  )
}
